import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { popBack, ToastProvider } from '@sf/ui';
import type { ToastTone } from '@sf/ui';
import { createCustomerClient, loadRuntimeConfig } from '@sf/api';
import { AppRoutes } from './app/routes';
import { ApiProvider } from './api/ApiProvider';
import { DemoReadOnlyToaster } from './demo/DemoReadOnlyToaster';
import { createQueryClient } from './api/queryClient';
import { RuntimeConfigProvider } from './config/RuntimeConfigProvider';
import { AuthProvider } from './auth';
import i18n, { initI18n } from './i18n';
import { watchInstallability } from './pwa/install';
import { registerServiceWorker } from './pwa/register';
import { IS_SHELL, SHELL_API_BASE_URL, SHELL_TENANT } from './shell';
import {
  haptic,
  syncSystemBars,
  watchBackButton,
  watchKeyboard,
  watchNotificationTaps,
  watchShellUrls,
} from './shell/bridge';
import { withNavTransition } from './app/nav';
import { isSolarRoot, isTabRoot } from './app/tabs';
import './index.css';

/**
 * What <html> carries before React sees it, and the seam every platform-specific rule hangs off:
 *
 *   data-platform="web" | "ios" | "android"   what this is running on
 *   data-shell="capacitor"                     present only inside a store app
 *
 * Two readers, one source. CSS branches on the attribute (`html[data-shell] &`,
 * `html[data-platform=ios] &`) and TypeScript reads it back through `shell/platform.ts`, so a rule and a
 * component can never disagree about the device. It has to be written before the first render: a tree that
 * mounts as "web" and becomes "ios" a frame later is the flash this whole exercise exists to remove.
 *
 * `web` is written first and unconditionally, so a shell whose Capacitor import fails for any reason still
 * has a defined platform. The import itself sits behind `IS_SHELL` — a build-time literal — which is why
 * the web bundle still contains no Capacitor code.
 */
async function markPlatform(): Promise<void> {
  const root = document.documentElement;
  root.dataset.platform = 'web';
  if (!IS_SHELL) return;

  root.dataset.shell = 'capacitor';
  try {
    const { Capacitor } = await import('@capacitor/core');
    const native = Capacitor.getPlatform();
    if (native === 'ios' || native === 'android') root.dataset.platform = native;
  } catch {
    /* a shell that cannot name its platform is treated as the web build, which is the safe half */
  }
}

/**
 * A toast is the app answering; on a phone it should be felt as well as read. The kit never imports
 * Capacitor — it only says which tone it just showed — and this is the whole mapping, in the one file
 * that already knows it is inside a shell.
 *
 * `success` and `error` are the two patterns both platforms define for exactly this, and a warning has
 * its own. `info` gets nothing on purpose: it is the tone the app uses for "here is a thing that
 * happened", and a buzz for every one of those is the noise that makes people turn haptics off.
 */
function toastHaptic(tone: ToastTone): void {
  if (tone === 'info') return;
  void haptic(tone);
}

/**
 * Boot order matters: runtime config (which API and which tenant) → i18n → API client → React.
 * Nothing here throws: a missing /config.json falls back to same-origin + tenant "solarfox".
 */
async function bootstrap() {
  // Before anything awaits: `beforeinstallprompt` fires during this first navigation, and an event nobody
  // was listening for is an install offer the customer never gets. Inside a store app there is nothing to
  // install — the customer already did.
  if (!IS_SHELL) watchInstallability();

  // The shell is served from the device, so there is no nginx to have written a /config.json next to it: the
  // one thing the web build learns at runtime is the one thing the app must already know.
  const [config] = await Promise.all([
    IS_SHELL
      ? Promise.resolve({ apiBaseUrl: SHELL_API_BASE_URL, tenant: SHELL_TENANT })
      : loadRuntimeConfig(),
    initI18n(),
  ]);
  document.documentElement.lang = i18n.resolvedLanguage ?? 'en';
  await markPlatform();
  // Immediately after, because it reads the attribute markPlatform just wrote: the status bar's glyphs have
  // to be legible against the page from the first frame, not from the frame after React decides on a theme.
  void syncSystemBars();

  const api = createCustomerClient(config);
  const queryClient = createQueryClient();

  const container = document.getElementById('root');
  if (!container) throw new Error('#root not found');

  createRoot(container).render(
    <StrictMode>
      <RuntimeConfigProvider config={config}>
        <ApiProvider client={api}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <ToastProvider onToast={toastHaptic}>
                {/* Turns the server's "the demo may not do that" into one toast, on every page. */}
                <DemoReadOnlyToaster />
                <AuthProvider>
                  <AppRoutes />
                </AuthProvider>
              </ToastProvider>
            </BrowserRouter>
          </QueryClientProvider>
        </ApiProvider>
      </RuntimeConfigProvider>
    </StrictMode>,
  );

  // Launch, layer 1 is over: React has the screen. The next frame is the first one it has painted, so the
  // boot mark and its unlayered background rules go then — not before (a gap of white), and not on a timer
  // (a gap of guesswork). Dropping the class matters as much as dropping the div: those rules outrank
  // @layer base, so a customer who has pinned the light theme on a dark phone would otherwise keep a navy
  // page underneath the app.
  requestAnimationFrame(() => {
    document.getElementById('boot')?.remove();
    document.documentElement.classList.remove('sf-booting');
  });
}

void bootstrap().finally(() => {
  if (IS_SHELL) {
    // No service worker: the bundle IS local, so a cache in front of it would only be a second copy of the
    // same files and a second thing that can be stale. The two listeners below are the shell's whole runtime.
    //
    // `history.pushState` + popstate rather than the router's navigate: bootstrap has already handed the tree
    // to React, and this is the one seam a plain module has onto a <BrowserRouter> without threading a ref
    // back out of it. The router listens to popstate, so it sees the route exactly as it would a Link.
    const go = (route: string) => {
      window.history.pushState(null, '', route);
      window.dispatchEvent(new PopStateEvent('popstate'));
    };
    void watchShellUrls(go);
    void watchNotificationTaps(go);

    // The keyboard writes <html data-keyboard="open"> and --sf-keyboard-h; everything that has to move out
    // of its way reads those, so there is nothing to do with the callback here.
    void watchKeyboard(() => {});

    // Android's hardware and gesture back, in one place. Registering a listener at all replaces Capacitor's
    // default (go back if the webview can, else finish the activity), so this handler owns every case
    // including leaving the app — an app whose back button does nothing at the root is a rejected app.
    //
    //   1. an overlay closes            the LIFO registry in @sf/ui: a Modal, a sheet, an open menu
    //   2. a leaf with history behind it   /billing, the wizard, a legal page: react-router keeps its
    //                                      position in history.state.idx, and idx > 0 is precisely "this
    //                                      session pushed at least one entry", which a plain
    //                                      `history.length` check cannot tell you
    //   3. anything that is not the Solar root   go home rather than out. A tab root goes home *without*
    //                                      consulting history — Android's convention is that tab taps
    //                                      build no back stack, so Alerts → Account → back → back leaves
    //                                      in two presses however many tabs were visited (verified on the
    //                                      emulator: history-first took four). A push notification's
    //                                      landing has no history behind it and still leads back in.
    //   4. the Solar root                leave, explicitly
    //
    // Step 2 sets data-nav='back' first, so the view transition runs backwards, and clears it again once
    // the transition can only be over. The system's own predictive-back animation is a different thing and
    // is not ours to draw — but this is *our* back, and it is. A flag left behind would make the NEXT
    // system POP look like ours.
    //
    // "The Solar root" is `/` *and* `/devices/<id>`, per spec §1: they are one tab, and `/` redirects to
    // the customer's inverter the moment it renders. Testing only for `/` sends a device page to `/`,
    // which bounces straight back — verified on the emulator — and the app can then never be left at all.
    // The wizard is the one Solar route that is not a root: back from its first step returns to the
    // inverter it was opened from (app/tabs.ts).
    void watchBackButton(() => {
      if (popBack()) return true;

      const pathname = window.location.pathname;
      const idx = (window.history.state as { idx?: number } | null)?.idx;
      if (!isTabRoot(pathname) && typeof idx === 'number' && idx > 0) {
        // Through app/nav.ts, so there is exactly one writer of <html data-nav> and one place that
        // knows when a transition is over: the flag is cleared by the transition's own `finished`,
        // not by a timer that has to guess. A system POP still arrives without the flag and keeps
        // the animation the OS draws for it.
        withNavTransition('back', () => window.history.back());
        return true;
      }

      if (!isSolarRoot(pathname)) {
        go('/');
        return true;
      }

      void import('@capacitor/app').then(({ App }) => App.exitApp());
      return true;
    });
    return;
  }
  // After the app is on screen, never before: the worker's install fetches must not compete with the
  // first paint or with /config.json.
  registerServiceWorker();
});
